<!-- RiskAcuityBadge.vue — Patient Flag chips for the CM worklist Risk/Acuity column.
     Every chip is a Patient Flag (row['flags'], attached by
     PatientWorkQueueService._attach_patient_flags). The old hardcoded chip logic
     (IP/OBS/ED tier from lace_score/ed_risk_score, EOL/ESRD/… cohort chips from
     condition_tags, A/B/C/D acuity from metadata.acuity) was REMOVED 2026-07-11:
     those signals are now Patient Flags (lace_score/echo_score categorical tier
     chips, condition flags, acuity flag) rendered here uniformly. See
     performance/patient_flags. -->
<template>
  <div class="flex flex-wrap gap-1">
    <span
      v-for="fl in patientFlags"
      :key="fl.key"
      class="relative inline-flex group/flag"
    >
      <span
        :class="fl.classes"
        class="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold border whitespace-nowrap"
      >{{ fl.label }}</span>
      <!-- Rich hover popover: the flag's underlying VALUE, formatted per the
           flag's flag_config.value_display (backend resolve_flag_display).
           Only shown when the flag declares a value_label. -->
      <span
        v-if="fl.valueLabel"
        class="pointer-events-none absolute bottom-full left-1/2 z-20 mb-1.5 hidden -translate-x-1/2 group-hover/flag:block"
        role="tooltip"
      >
        <span class="block rounded-md bg-gray-900 px-2.5 py-1.5 text-xs font-medium text-white shadow-lg whitespace-nowrap">
          <span class="block font-semibold">{{ fl.label }}</span>
          <span class="block text-gray-300">{{ fl.valueLabel }}</span>
          <span class="absolute left-1/2 top-full -translate-x-1/2 border-4 border-transparent border-t-gray-900"></span>
        </span>
      </span>
    </span>
    <span v-if="!patientFlags.length" class="text-xs text-gray-400">—</span>
  </div>
</template>

<script>
// Patient Flag colors — set on each PatientFlag.color (boolean flag) or on each
// flag_config.values[].color (categorical flag). resolve_flag_display picks the
// color + style ('light' | 'solid') per row; this maps them to Tailwind classes.
// LIGHT is the default chip (risk tiers, acuity). SOLID (bg-*-600/white) matches
// the legacy VC+ cohort chips (EOL/ESRD/MCC/SPV/COPD/CHF/DM) exactly — flags
// declare flag_config.style='solid'. See performance/patient_flags.
// Three fill intensities, keyed by flag `style`:
//   solid  = bg-*-600 / white   (legacy High tier + VC+ cohort chips)
//   light  = bg-*-100 / *-700   (default; legacy Med tier)
//   faint  = bg-*-50  / *-500   (legacy Low tier)
// The episode is encoded in the COLOR (IP=violet, OBS=amber, ED=rose) and the
// tier in the STYLE — matching the legacy STYLE_MAP exactly. See patient_flags.
const FLAG_COLOR_LIGHT = {
  red:     'bg-red-100 text-red-700 border-red-300',
  orange:  'bg-orange-100 text-orange-700 border-orange-300',
  amber:   'bg-amber-100 text-amber-700 border-amber-300',
  yellow:  'bg-yellow-100 text-yellow-700 border-yellow-300',
  blue:    'bg-blue-100 text-blue-700 border-blue-300',
  sky:     'bg-sky-100 text-sky-700 border-sky-300',
  teal:    'bg-teal-100 text-teal-700 border-teal-300',
  emerald: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  indigo:  'bg-indigo-100 text-indigo-700 border-indigo-300',
  violet:  'bg-violet-100 text-violet-700 border-violet-300',
  purple:  'bg-purple-100 text-purple-700 border-purple-300',
  pink:    'bg-pink-100 text-pink-700 border-pink-300',
  rose:    'bg-rose-100 text-rose-700 border-rose-300',
  gray:    'bg-gray-100 text-gray-600 border-gray-300',
};
const FLAG_COLOR_SOLID = {
  red:     'bg-red-600 text-white border-red-600',
  orange:  'bg-orange-600 text-white border-orange-600',
  amber:   'bg-amber-600 text-white border-amber-600',
  yellow:  'bg-yellow-600 text-white border-yellow-600',
  blue:    'bg-blue-600 text-white border-blue-600',
  sky:     'bg-sky-600 text-white border-sky-600',
  teal:    'bg-teal-600 text-white border-teal-600',
  emerald: 'bg-emerald-600 text-white border-emerald-600',
  indigo:  'bg-indigo-600 text-white border-indigo-600',
  violet:  'bg-violet-600 text-white border-violet-600',
  purple:  'bg-purple-600 text-white border-purple-600',
  pink:    'bg-pink-600 text-white border-pink-600',
  rose:    'bg-rose-600 text-white border-rose-600',
  gray:    'bg-gray-600 text-white border-gray-600',
};
const FLAG_COLOR_FAINT = {
  red:     'bg-red-50 text-red-500 border-red-200',
  orange:  'bg-orange-50 text-orange-500 border-orange-200',
  amber:   'bg-amber-50 text-amber-500 border-amber-200',
  yellow:  'bg-yellow-50 text-yellow-500 border-yellow-200',
  blue:    'bg-blue-50 text-blue-500 border-blue-200',
  sky:     'bg-sky-50 text-sky-500 border-sky-200',
  teal:    'bg-teal-50 text-teal-500 border-teal-200',
  emerald: 'bg-emerald-50 text-emerald-500 border-emerald-200',
  indigo:  'bg-indigo-50 text-indigo-500 border-indigo-200',
  violet:  'bg-violet-50 text-violet-500 border-violet-200',
  purple:  'bg-purple-50 text-purple-500 border-purple-200',
  pink:    'bg-pink-50 text-pink-500 border-pink-200',
  rose:    'bg-rose-50 text-rose-500 border-rose-200',
  gray:    'bg-gray-50 text-gray-500 border-gray-200',
};
const FLAG_STYLE_MAPS = { solid: FLAG_COLOR_SOLID, light: FLAG_COLOR_LIGHT, faint: FLAG_COLOR_FAINT };

export default {
  name: 'RiskAcuityBadge',
  props: {
    patient: { type: Object, required: true }
  },
  computed: {
    // Patient Flags attached to the row by _attach_patient_flags (same definition
    // as return_patient_flags), gated per-opportunity + sorted by priority asc.
    // For Vytalize these are: lace_score / echo_score (categorical IP-High…ED-Low
    // tier chips), risk_level, acuity (A/B/C/D), and the condition flags
    // (EOL/ESRD/MCC/SPV/COPD/CHF/DM/UWF). Tenant-agnostic — any tenant's flags
    // render here with no hardcoded logic.
    patientFlags() {
      const flags = this.patient.flags;
      if (!Array.isArray(flags)) return [];
      return flags.map(fl => {
        const map = FLAG_STYLE_MAPS[fl.style] || FLAG_COLOR_LIGHT;
        return {
          key: fl.key || fl.label,
          label: fl.label || fl.key,
          classes: map[fl.color] || map.gray,
          valueLabel: fl.value_label || null,
        };
      });
    }
  }
};
</script>
